import {Injectable, NgZone} from '@angular/core';
import {Platform} from '@ionic/angular';
import {
    PushNotifications,
    Token,
    PushNotificationSchema,
    ActionPerformed,
    PermissionStatus,
} from '@capacitor/push-notifications';
import {BehaviorSubject} from 'rxjs';
import {Capacitor, registerPlugin, PluginListenerHandle} from '@capacitor/core';

type AnyMap = Record<string, any>;

// Mensaje normalizado para uso interno (opcional; mantiene raw por compatibilidad)
interface PushMessage {
    title?: string;
    body?: string;
    image?: string;
    data?: AnyMap;
    raw?: AnyMap;
    ts?: number;
    foreground?: boolean;
    presentation?: string;
}

function normalizeApnsPayload(
    p: AnyMap,
    meta?: { foreground?: boolean; presentation?: string }
): PushMessage {
    const aps = p?.aps ?? {};
    const alert = aps?.alert ?? {};
    const tsRaw = p['google.c.a.ts'] ?? p['ts'] ?? (Date.now() / 1000);
    const ts = Number(tsRaw);
    const image = p?.fcm_options?.image;

    return {
        title: alert?.title,
        body: alert?.body,
        image,
        data: p,
        raw: p,
        ts: Number.isFinite(ts) ? ts : undefined,
        foreground: meta?.foreground,
        presentation: meta?.presentation,
    };
}

// Tipado del puente nativo (Capacitor iOS)
interface PushBridgeApi {
    addListener?: (
        eventName: string,
        cb: (data: AnyMap) => void
    ) => PluginListenerHandle | Promise<PluginListenerHandle>;
    getInitialNotification?: () => Promise<{ notification?: AnyMap } | undefined>;
}

// Intentamos registrar el plugin nativo por nombre (Capacitor v5+). Fallback a window.Capacitor.Plugins
const pushBridgeApi: PushBridgeApi | null = (() => {
    try {
        const p = registerPlugin<PushBridgeApi>('PushBridge');
        if (p) return p;
    } catch { /* ignore */
    }
    const plugins = (window as any)?.Capacitor?.Plugins ?? (Capacitor as any)?.Plugins;
    return (plugins?.PushBridge ?? plugins?.PushBridgePlugin) || null;
})();

@Injectable({providedIn: 'root'})
export class PushNotificationService {
    private _token$ = new BehaviorSubject<string | null>(null);
    token$ = this._token$.asObservable();

    private _inited = false;
    private _bridgeHandles: PluginListenerHandle[] = [];

    private _message$ = new BehaviorSubject<PushNotificationSchema | AnyMap | null>(null);
    message$ = this._message$.asObservable();

    private _action$ = new BehaviorSubject<ActionPerformed | AnyMap | null>(null);
    action$ = this._action$.asObservable();

    // referencia opcional al bridge nativo (iOS) para eventos más ricos
    private get pushBridge(): PushBridgeApi | null {
        return pushBridgeApi;
    }

    /** Escucha los logs inyectados desde AppDelegate en la WebView y los reemite a los streams */
    private attachNativeLogTap(): void {
        const w = window as any;
        if (!w || w.__nativePushLogAttached === true) return;
        w.__nativePushLogAttached = true;

        window.addEventListener('nativePushLog', (ev: any) => {
            try {
                const detail = ev?.detail || {};
                const label: string = detail.label || 'unknown';
                const payload: AnyMap = detail.payload || {};
                console.log('[PUSH][native→JS:event]', label, payload);

                // enrutar a subjects si el label coincide
                if (label.startsWith('token.')) {
                    const t = payload.token ?? payload.value ?? null;
                    this.zone.run(() => this._token$.next(t));
                } else if (label.startsWith('notificationReceived.')) {
                    const notif = payload.notification ?? payload;
                    this.zone.run(() => this._message$.next(notif));
                } else if (label.startsWith('notificationActionPerformed.')) {
                    const act = payload.notification ?? payload;
                    this.zone.run(() => this._action$.next(act));
                }
            } catch (e) {
                console.warn('[PUSH][native→JS:event] handler error', e);
            }
        });
    }

    constructor(private platform: Platform, private zone: NgZone) {
        // Adjunta cuanto antes el "tap" de logs nativos hacia la consola de Safari
        this.attachNativeLogTap();
    }

    /** Helper para registrar listeners del puente nativo manejando retorno sync/async */
    private async addBridgeListener(eventName: string, cb: (data: AnyMap) => void): Promise<void> {
        const bridge = this.pushBridge as any;
        if (!bridge || typeof bridge.addListener !== 'function') return;
        const ret = bridge.addListener(eventName, cb);
        const handle = (ret && typeof (ret as Promise<any>).then === 'function')
            ? await (ret as Promise<PluginListenerHandle>).catch(() => undefined)
            : (ret as PluginListenerHandle);
        if (handle?.remove) this._bridgeHandles.push(handle);
    }

    /** Llamar idealmente en app start/login */
    async init(): Promise<void> {
        if (!this.platform.is('capacitor')) return;
        if (this._inited) return;   // ← guard
        this._inited = true;

        console.log('[PUSH:init] Platform:', this.platform.platforms());
        this.attachNativeLogTap(); // idempotente; asegura listener incluso si el constructor aún no corrió

        // 0) Si existe el puente nativo, encadenamos listeners *antes* de pedir permisos
        //    para no perder eventos de arranque (p. ej. notificación que abrió la app).
        await this.bindNativeBridgeIfAvailable();
        // Adjunta consola para ver cambios de los streams en Safari
        this.debugAttachConsole();
        console.info('[PUSH:init] bridge presente:', !!this.pushBridge);

        // 1) Permisos
        const perm: PermissionStatus = await PushNotifications.checkPermissions();
        console.log('[PUSH:perm] estado inicial:', perm);
        let granted = perm.receive === 'granted';
        if (!granted) {
            const req = await PushNotifications.requestPermissions();
            console.log('[PUSH:perm] respuesta requestPermissions:', req);
            granted = req.receive === 'granted';
        }
        if (!granted) {
            console.warn('[PUSH] Permiso denegado por el usuario');
            return;
        }

        // 2) (Android) Crear canal por defecto para asegurar recepción en foreground/background
        if (this.platform.is('android')) {
            try {
                await PushNotifications.createChannel({id: 'default', name: 'General'});
                console.log('[PUSH:android] Canal "default" listo');
            } catch (e) {
                console.log('[PUSH:android] Canal ya existía / no-op');
            }
        }

        // 3) Registro nativo (APNs/FCM)
        console.log('[PUSH:register] registrando APNs/FCM...');
        await PushNotifications.register();

        // 4) Listeners del plugin oficial (fallback / Android)
        await PushNotifications.addListener('registration', async (t: Token) => {
            // Android: suele ser FCM token; iOS: APNs token
            const token = t.value;
            this.zone.run(() => this._token$.next(token));
            console.log('[PUSH] registration → token (Capacitor):', token);
            // TODO: envía el token a tu backend si corresponde
        });

        await PushNotifications.addListener('registrationError', (err) => {
            console.error('[PUSH] registrationError:', err);
        });

        await PushNotifications.addListener('pushNotificationReceived', (notif: PushNotificationSchema) => {
            this.zone.run(() => this._message$.next(notif));
            console.log('[PUSH] pushNotificationReceived (Capacitor/foreground):', JSON.stringify(notif));
        });

        await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
            this.zone.run(() => this._action$.next(action));
            console.log('[PUSH] pushNotificationActionPerformed (Capacitor):', JSON.stringify(action));
        });
    }

    /**
     * Vincula listeners al puente nativo iOS (si existe) que reenvía:
     *  - token            → { value: string }
     *  - notificationReceived → { notification: any }
     *  - notificationActionPerformed → { notification: any }
     *  - getInitialNotification() → { notification?: any }
     */
    private async bindNativeBridgeIfAvailable() {
        const bridge = this.pushBridge;
        if (!bridge) {
            console.log('[PUSH:bridge] No hay PushBridge nativo (continuará con plugin oficial)');
            return;
        }

        console.log('[PUSH:bridge] Detectado PushBridge nativo. Enlazando eventos...');

        // Token unificado (APNs/FCM) desde el bridge
        await this.addBridgeListener('token', (data: AnyMap) => {
            const value = data?.value ?? data?.token ?? null;
            this.zone.run(() => this._token$.next(value));
            console.log('[PUSH:bridge] token:', value);
        });

        // Notificación recibida (foreground/background silent)
        await this.addBridgeListener('notificationReceived', (data: AnyMap) => {
            const notif = data?.notification ?? data;
            const norm = normalizeApnsPayload(notif, {foreground: true, presentation: 'bridge.notificationReceived'});
            this.zone.run(() => this._message$.next(norm as any));
            console.log('[PUSH:bridge] notificationReceived:', norm);
        });

        // Acción del usuario al tocar la notificación
        await this.addBridgeListener('notificationActionPerformed', (data: AnyMap) => {
            const action = data?.notification ?? data;
            const norm = normalizeApnsPayload(action, {presentation: 'bridge.notificationActionPerformed'});
            this.zone.run(() => this._action$.next(norm as any));
            console.log('[PUSH:bridge] notificationActionPerformed:', norm);
        });

        // Si la app se abrió tocando una notificación, recuperarla
        try {
            const initial = await bridge.getInitialNotification?.();
            if (initial?.notification) {
                this.zone.run(() => this._action$.next(initial.notification));
                console.log('[PUSH:bridge] initialNotification:', JSON.stringify(initial.notification));
            } else {
                console.log('[PUSH:bridge] initialNotification: none');
            }
        } catch (e) {
            console.warn('[PUSH:bridge] getInitialNotification error:', e);
        }
        console.info('[PUSH:bridge] listeners registrados y listos');
    }

    // /** Suscripción a topics (si existe plugin Firebase Messaging) */
    // async subscribe(topic: string): Promise<void> {
    //   try {
    //     const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    //     await FirebaseMessaging.subscribeToTopic({ topic });
    //     console.log(`[PUSH] Subscrito a topic: ${topic}`);
    //   } catch {
    //     console.warn('[PUSH] Plugin @capacitor-firebase/messaging no instalado; no se puede usar topics.');
    //   }
    // }
    //
    // async unsubscribe(topic: string): Promise<void> {
    //   try {
    //     const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    //     await FirebaseMessaging.unsubscribeFromTopic({ topic });
    //     console.log(`[PUSH] Desubscrito de topic: ${topic}`);
    //   } catch {
    //     console.warn('[PUSH] Plugin @capacitor-firebase/messaging no instalado; no se puede usar topics.');
    //   }
    // }
    //
    // /** Devuelve FCM token si el plugin está disponible */
    // private async tryGetFcmToken(): Promise<string | null> {
    //   try {
    //     const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    //     const { token } = await FirebaseMessaging.getToken();
    //     return token ?? null;
    //   } catch {
    //     return null;
    //   }
    // }

    // private async sendTokenToBackend(token: string) { ... }

    /** Imprime en consola los cambios de los streams para depurar integración */
    public debugAttachConsole(): void {
        this.token$.subscribe(v => {
            console.group('[PUSH][debug] token$');
            console.dir(v);
            console.groupEnd();
        });
        this.message$.subscribe(v => {
            console.group('[PUSH][debug] message$');
            console.dir(v);
            console.groupEnd();
        });
        this.action$.subscribe(v => {
            console.group('[PUSH][debug] action$');
            console.dir(v);
            console.groupEnd();
        });
    }

    public destroy(): void {
        for (const h of this._bridgeHandles) {
            try {
                h.remove();
            } catch {
            }
        }
        this._bridgeHandles = [];
        this._inited = false;
    }
}
